import React from 'react'
import styled, {css} from 'styled-components'
import {ChevronRight} from 'lucide-react'

export interface BreadcrumbItem {
  label: string
  href?: string
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[]
  currentHeadingLevel?: 1 | 2
  'data-testid'?: string
}

const BreadcrumbNav = styled.nav<{$asTitle: boolean}>`
  min-width: 0;
  font-family: var(--font-sans);
  font-size: ${({$asTitle}) => ($asTitle ? 'var(--fs-h2)' : 'var(--fs-body)')};

  ol {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    min-width: 0;
    margin: 0;
    padding: 0;
    list-style: none;
    white-space: nowrap;
  }

  li {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    min-width: 0;
  }
`

const crumbText = css`
  display: block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const BreadcrumbLink = styled.a`
  ${crumbText}
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: var(--brand-cta);
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: var(--brand-cta-hover);
    text-decoration: underline;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
    border-radius: var(--radius-sm);
  }
`

const BreadcrumbLabel = styled.span<{$current?: boolean}>`
  ${crumbText}
  margin: 0;
  font-size: inherit;
  color: ${({$current}) => ($current ? 'var(--text-primary)' : 'var(--text-secondary)')};
  font-weight: ${({$current}) => ($current ? 'var(--fw-semibold)' : 'inherit')};
`

const BreadcrumbSeparator = styled(ChevronRight)`
  flex-shrink: 0;
  color: var(--text-secondary);
`

export const Breadcrumb = ({
  items,
  currentHeadingLevel,
  'data-testid': testId,
}: BreadcrumbProps) => (
  <BreadcrumbNav aria-label="Breadcrumb" data-testid={testId} $asTitle={!!currentHeadingLevel}>
    <ol>
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1
        if (isLast) {
          return (
            <li key={`${item.label}-${idx}`}>
              <BreadcrumbLabel
                $current
                as={currentHeadingLevel ? `h${currentHeadingLevel}` : 'span'}
                aria-current="page"
                title={item.label}>
                {item.label}
              </BreadcrumbLabel>
            </li>
          )
        }
        return (
          <li key={`${item.label}-${idx}`}>
            {item.href ? (
              <BreadcrumbLink href={item.href} onClick={item.onClick} title={item.label}>
                {item.label}
              </BreadcrumbLink>
            ) : item.onClick ? (
              <BreadcrumbLink as="button" type="button" onClick={item.onClick} title={item.label}>
                {item.label}
              </BreadcrumbLink>
            ) : (
              <BreadcrumbLabel title={item.label}>{item.label}</BreadcrumbLabel>
            )}
            <BreadcrumbSeparator size={16} aria-hidden="true" />
          </li>
        )
      })}
    </ol>
  </BreadcrumbNav>
)
